// src/App.js

import React from "react"
import { PAGES, YOUTUBE_CHANNEL } from "./data"
import "./styles.css"

const CARD_LOGO = "/brand/shades-circle-logo.png" // only in header

// Sheet URL
const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1SKGSfIJbzuhOBRnHcX1lijfsKrgO1kFrHaQ7yLyeGko/gviz/tq?tqx=out:json&gid=2004738771"

const COL_PAGE_NAME = "Page Name"
const COL_STATUS = "Status"
const COL_TITLE = "News Title"
const COL_DESCRIPTION = "Description"

const normalize = str => (str || "").toString().trim().toLowerCase()

function truncateWords(text, maxWords = 30) {
  if (!text) return ""
  const words = text.trim().split(/\s+/)
  if (words.length <= maxWords) return text
  return words.slice(0, maxWords).join(" ") + "..."
}

// shared helper to get videos for a page, with optional query filter
function filterVideosForPage(page, videos, query) {
  if (!page || !videos.length) return []

  const target = normalize(page.code)
  const q = (query || "").toLowerCase()

  const filtered = videos.filter(row => {
    const pageName = normalize(row[COL_PAGE_NAME])
    const statusRaw = (row[COL_STATUS] || "").toUpperCase()
    const hasTitle = !!row[COL_TITLE]
    const hasDesc = !!row[COL_DESCRIPTION]

    const isReadyOrPosted =
      statusRaw === "READY" || statusRaw === "POSTED"

    if (
      pageName !== target ||
      !isReadyOrPosted ||
      !hasTitle ||
      !hasDesc
    ) {
      return false
    }

    if (!q) return true

    const title = (row[COL_TITLE] || "").toString().toLowerCase()
    const desc = (row[COL_DESCRIPTION] || "")
      .toString()
      .toLowerCase()

    return title.includes(q) || desc.includes(q)
  })

  // last 60, newest first
  return filtered.slice(-60).reverse()
}

// check if any news row for this page matches query
function hasNewsMatchForPage(page, query, rows) {
  if (!query) return false
  if (!rows.length) return false

  const q = query.toLowerCase()
  const target = normalize(page.code)

  return rows.some(row => {
    const pageName = normalize(row[COL_PAGE_NAME])
    const statusRaw = (row[COL_STATUS] || "").toUpperCase()
    const title = (row[COL_TITLE] || "").toString()
    const desc = (row[COL_DESCRIPTION] || "").toString()

    const isReadyOrPosted =
      statusRaw === "READY" || statusRaw === "POSTED"

    if (pageName !== target || !isReadyOrPosted) return false
    if (!title || !desc) return false

    return (
      title.toLowerCase().includes(q) ||
      desc.toLowerCase().includes(q)
    )
  })
}

// home page section for each region
function HomePageSection({ page, videos, query, onOpen }) {
  const pageVideos = React.useMemo(
    () => filterVideosForPage(page, videos, query),
    [page, videos, query]
  )

  // duplicate list for seamless loop on desktop
  const marqueeVideos =
    pageVideos.length > 0 ? [...pageVideos, ...pageVideos] : []

  // consistent speed on desktop
  const baseSpeedPxPerSec = 40 // global speed
  const perCardWidth = 300 // card width + gap (approx)
  const cardCount = Math.max(pageVideos.length, 1)
  const distancePx = 0.5 * cardCount * perCardWidth
  const durationSeconds = Math.max(18, distancePx / baseSpeedPxPerSec)

  // mobile detection
  const [isMobile, setIsMobile] = React.useState(false)

  React.useEffect(() => {
    const check = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth <= 640)
      }
    }
    check()
    window.addEventListener("resize", check)
    return () => window.removeEventListener("resize", check)
  }, [])

  // mobile slider index
  const [mobileIndex, setMobileIndex] = React.useState(0)

  React.useEffect(() => {
    setMobileIndex(0)
  }, [page.code, query, pageVideos.length])

  const handlePrev = () => {
    if (!pageVideos.length) return
    setMobileIndex(i => (i === 0 ? pageVideos.length - 1 : i - 1))
  }

  const handleNext = () => {
    if (!pageVideos.length) return
    setMobileIndex(i =>
      i === pageVideos.length - 1 ? 0 : i + 1
    )
  }

  return (
    <section className="page-section">
      <div className="page-section-header">
        <button
          className="page-title-btn"
          onClick={() => onOpen(page)}
        >
          {page.name}
        </button>

        <div className="page-links">
          <a
            href={page.link}
            target="_blank"
            rel="noreferrer"
            className="instagram"
            aria-label={`${page.name} on Instagram`}
          >
            <i className="fa-brands fa-instagram"></i>
          </a>

          {page.facebook && (
            <a
              href={page.facebook}
              target="_blank"
              rel="noreferrer"
              className="facebook"
              aria-label={`${page.name} on Facebook`}
            >
              <i className="fa-brands fa-facebook-f"></i>
            </a>
          )}
        </div>
      </div>

      <p className="page-subline">
        {page.coverage} · {page.blurb}
      </p>

      {pageVideos.length === 0 ? (
        <p className="page-empty-text">
          No stories yet from this region.
        </p>
      ) : isMobile ? (
        // mobile slider, no auto move
        <div className="mobile-slider">
          <button
            className="slider-btn slider-btn-left"
            type="button"
            onClick={handlePrev}
          >
            ‹
          </button>

          <div className="mobile-card-window">
            {pageVideos.length > 0 && (() => {
              const v = pageVideos[mobileIndex]
              const title = v[COL_TITLE]
              const desc = v[COL_DESCRIPTION] || ""
              const truncatedDesc = truncateWords(desc, 30)
              const showReadMore = truncatedDesc !== desc

              return (
                <a
                  className="news-card"
                  href={page.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <h4 className="news-card-title">{title}</h4>
                  <p className="news-card-desc">
                    {truncatedDesc}
                    {showReadMore && (
                      <span className="read-more"> Read more</span>
                    )}
                  </p>
                </a>
              )
            })()}
          </div>

          <button
            className="slider-btn slider-btn-right"
            type="button"
            onClick={handleNext}
          >
            ›
          </button>
        </div>
      ) : (
        // desktop marquee
        <div className="marquee-shell">
          <div
            className="marquee-track"
            style={{ "--marquee-duration": `${durationSeconds}s` }}
          >
            {marqueeVideos.map((v, i) => {
              const title = v[COL_TITLE]
              const desc = v[COL_DESCRIPTION] || ""
              const truncatedDesc = truncateWords(desc, 30)
              const showReadMore = truncatedDesc !== desc

              return (
                <a
                  key={i}
                  className="news-card marquee-card"
                  href={page.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <h4 className="news-card-title">{title}</h4>
                  <p className="news-card-desc">
                    {truncatedDesc}
                    {showReadMore && (
                      <span className="read-more"> Read more</span>
                    )}
                  </p>
                </a>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}

// detail page (when a section heading is clicked)
function ShadeDetail({ page }) {
  return (
    <section className="country-detail">
      <h2 className="country-title">{page.name}</h2>
      <p className="country-sub">
        Region page from Shades Network, sharp headlines and context for{" "}
        {page.coverage.toLowerCase()}.
      </p>

      <div className="meta meta-detail">
        <div>
          <span className="k">Instagram</span>
          <span className="v">@{page.username}</span>
        </div>
        <div>
          <span className="k">Coverage</span>
          <span className="v">{page.coverage}</span>
        </div>
        <div>
          <span className="k">Includes</span>
          <span className="v">{page.includes}</span>
        </div>
      </div>

      <div className="actions">
        <a
          className="social-btn instagram"
          href={page.link}
          target="_blank"
          rel="noreferrer"
        >
          <i className="fa-brands fa-instagram"></i>
        </a>

        {page.facebook && (
          <a
            className="social-btn facebook"
            href={page.facebook}
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa-brands fa-facebook-f"></i>
          </a>
        )}
      </div>
    </section>
  )
}

function Header({ value, onChange, onHome }) {
  return (
    <header className="header">
      <div className="header-inner">
        <div className="brand-wrap" onClick={onHome}>
          <img src={CARD_LOGO} className="brand-logo" alt="" />
          <div className="brand-text">
            <h1 className="site-title">Shades Network</h1>
            <p className="tag">One network, many regions</p>
          </div>
        </div>
        <div className="header-right">
          <label className="search">
            <input
              type="search"
              placeholder="Search page, region or news"
              value={value}
              onChange={e => onChange(e.target.value)}
            />
          </label>
          <a
            className="yt-link"
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noreferrer"
          >
            <i className="fa-brands fa-youtube"></i>
            YouTube
          </a>
        </div>
      </div>
    </header>
  )
}

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <div>© {new Date().getFullYear()} Shades Network</div>
        <div className="footer-nav">
          <a
            href={YOUTUBE_CHANNEL}
            target="_blank"
            rel="noreferrer"
          >
            YouTube
          </a>
          <a
            href="https://www.instagram.com/network.shades/"
            target="_blank"
            rel="noreferrer"
          >
            Network.shades
          </a>
        </div>
      </div>
    </footer>
  )
}

export default function App() {
  const [query, setQuery] = React.useState("")
  const [active, setActive] = React.useState(null)
  const [sheetRows, setSheetRows] = React.useState([])

  React.useEffect(() => {
    async function load() {
      const res = await fetch(SHEET_URL)
      const text = await res.text()
      const jsonStr = text.substring(
        text.indexOf("{"),
        text.lastIndexOf("}") + 1
      )
      const data = JSON.parse(jsonStr)
      const headers = data.table.cols.map(c => c.label || c.id)

      const parsed = data.table.rows.map(r => {
        const row = {}
        r.c.forEach((cell, i) => {
          row[headers[i]] = cell ? cell.f || cell.v || "" : ""
        })
        return row
      })

      setSheetRows(parsed)
    }
    load()
  }, [])

  const filteredPages = React.useMemo(() => {
    // remove Network.shades from home data
    const visiblePages = PAGES.filter(p => p.code !== "network")

    if (!query) return visiblePages

    const q = query.toLowerCase()

    return visiblePages.filter(p => {
      const pageMatch =
        p.name.toLowerCase().includes(q) ||
        p.coverage.toLowerCase().includes(q) ||
        p.includes.toLowerCase().includes(q)

      const newsMatch = hasNewsMatchForPage(p, query, sheetRows)

      return pageMatch || newsMatch
    })
  }, [query, sheetRows])

  return (
    <div className="app">
      <Header
        value={query}
        onChange={setQuery}
        onHome={() => setActive(null)}
      />

      {!active && (
        <main className="home-wrap">
          <section className="home-intro">
            <h1 className="home-intro-title">Shades Network</h1>
            <p className="home-intro-sub">
              One place to skim sharp headlines from every Shades region.
              Browse the sections below or search by page, region or story.
            </p>
          </section>

          {filteredPages.map(page => (
            <HomePageSection
              key={page.code}
              page={page}
              videos={sheetRows}
              query={query}
              onOpen={setActive}
            />
          ))}
        </main>
      )}

      {active && (
        <main className="detail-wrap">
          <button
            className="link-back"
            onClick={() => setActive(null)}
          >
            ← All pages
          </button>
          <ShadeDetail page={active} />
        </main>
      )}

      <Footer />
    </div>
  )
}
